import { Cartesian3, Resource } from "@cesium/engine";
import Utils from "./Utils";

/**
 * Vworld API 통한 지오코딩 제공.</br>
 * Provides geocoding through Vworld Api.
 *
 * @alias DitapVworldGeocoderService
 * @constructor
 *
 * @param {object} object 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Element|string} object.container 위젯을 포함할 DOM 요소 또는 ID.</br> The DOM element or ID that will contain the widget.
 * @param {string} key Vworld에서 발급한 API 키.</br> API key issued by Vworldcontain the widget.
 *
 * @example
 * const geocoderService = new Ditap.DitapVworldGeocoderService("vworld 발급 api key");
 */
class DitapVworldGeocoderService {
  constructor(key) {
    this._url = `${Utils.getProtocol()}//api.vworld.kr/req/search`;
    this._key = key;
    this._resource = new Resource({
      url: this.url,
      queryParameters: {
        key: key,
        request: "search",
        type: "address",
        category: "road",
        format: "json",
        size: 10,
        page: 1,
      },
    });
  }

  /*
   * 지오코더 서비스 요청 Url.</br>
   * Geocoder Service Request Url.
   * @type {string}
   * @see {@link https://www.vworld.kr/dev/v4dv_search2_s001.do}
   */
  set url(url) {
    this._url = url;
  }
  get url() {
    return this._url;
  }

  /**
   * 지오코더 서비스 발급 API 키.</br>
   * Geocoder service issuance Api key.
   * @type {string}
   * @see {@link https://www.vworld.kr/dev/v4api.do}
   */
  set key(key) {
    this._key = key;
  }
  get key() {
    return this._key;
  }

  /*
   * 지오코더 서비스 요청 리소스.</br>
   * Geocoder Service Request Resource.
   * @type {object}
   * @see {@link https://www.vworld.kr/dev/v4dv_search2_s001.do}
   */
  set resource(object) {
    this._resource = new Resource({
      url: object.url,
      queryParameters: object.queryParameters,
    });
  }
  get resource() {
    return this._resource;
  }

  /*
   * 지오코더 서비스 요청 Url 변경.</br>
   * Change the geocoder service request url.
   * @param {string} url 변경할 url.</br> geocoder service request url.
   */
  changeResourceUrl(url) {
    this._resource.url = url;
  }

  /*
   * 지오코더 서비스 요청 페이지 번호 변경.</br>
   * Change the geocoder service request page number.
   * @param {number} page 변경할 페이지 번호.<br> Page number to change.
   */
  changeResourcePage(page) {
    this._resource.queryParameters.page = page;
  }

  /*
   * 지오코더 서비스 수행.</br>
   * Perform geocoder service.
   * @param {string} query 검색 키워드.</br> Search keyworld.
   * @param {boolean} [newxtPage] 다음 페이지 조회 상태.</br> Next Page Lookup Status.
   * @returns {Promise.<Array.<GeocoderService.Result>>} Vwolrd Api에서 얻은 결과.</br> results from Vworld Api.
   * @see {@link https://www.vworld.kr/dev/v4dv_search2_s001.do}
   */
  async geocode(query, nextPage) {
    const resource = this._resource;
    resource.queryParameters.query = query;

    if (nextPage) {
      this.changeResourcePage(this.resource.queryParameters.page + 1);
    } else {
      this.changeResourcePage(1);
    }

    return resource.fetchJsonp().then(function (result) {
      if (resource.queryParameters.request === "search") {
        let returnResult = [];
        if (result.response.result) {
          const itemArray = result.response.result.items;
          if (itemArray.length === 0) {
            return result;
          }

          returnResult = itemArray.map(function (resource) {
            const point = resource.point;
            const x = Number(point.x);
            const y = Number(point.y);

            const cartesian = Cartesian3.fromDegrees(x, y);
            const result = {
              displayName: `${resource.address.bldnm} ${resource.address.bldnmdc}`,
              destination: cartesian,
              road: `${resource.address.road} ${resource.address.bldnmdc}`,
            };
            return result;
          });
        } else if (result.response?.status === "ERROR") {
          if (result.response?.error?.code === "INVALID_KEY") {
            return "INVALID_KEY";
          } else if (result.response?.error?.code === "EXPIRE_KEY") {
            return "EXPIRE_KEY";
          }
        }
        return returnResult;
      }
    });
  }

  destroy() {
    this.url = undefined;
    this.key = undefined;
    this.resource = undefined;
  }
}
export default DitapVworldGeocoderService;
